import type { Job } from "@eai-toolkit/client";
import { JobState } from "@eai-toolkit/client";
import { HighlightOff, Replay } from "@mui/icons-material";
import VpnKeyOutlined from "@mui/icons-material/VpnKeyOutlined";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Snackbar,
} from "@mui/material";
import { tracker } from "@openreplay/tracker";
import { getErrorMessage } from "@store/api/baseApi";
import {
  JOB_PERMISSIONS,
  useCancelJobMutation,
  useGetJobsPermissionLevelsQuery,
  useGetJobsQuery,
  useGetJobsTotalQuery,
  useRetryJobMutation,
} from "@store/api/jobsApi";
import { useGetResourceMetadataQuery } from "@store/api/resourceApi";
import { useGetMeQuery } from "@store/api/usersApi";
import { useAppDispatch } from "@store/hooks";
import { useFilters } from "@store/hooks/useFilters";
import { useQueryStateFromUrl } from "@store/hooks/useQueryStateFromUrl";
import { useTableControls } from "@store/hooks/useTableControls";
import { useUpdateQueryState } from "@store/hooks/useUpdateQueryState";
import {
  dehydrateJobsQueryState,
  dehydrateJobsQueryStateToString,
  hydrateJobsQueryState,
} from "@store/queryState";
import { resetNewJobForm } from "@store/slices/newJobFormSlice";
import React, { useCallback, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  DEFAULT_CHIPS,
  LoadOverlay,
  QuickFilters,
  ViewWrapper,
} from "@components";
import { AccountSelector } from "@components/AccountSelector/AccountSelector";
import { DataTable, useDataTableUrlInit } from "@components/DataTable";
import {
  NameIdSearch,
  type SearchFilter,
} from "@components/NameIdSearch/NameIdSearch";
import type { FilterName } from "@components/QuickFilters/QuickFilters";
import StarredFilterChip from "@components/StarredFilterChip/StarredFilterChip";
import { StateSelector } from "@components/StateSelector/StateSelector";
import { UserRoleSelector } from "@components/UserRoleSelector/UserRoleSelector";
import {
  useValidationContextValue,
  ValidationContext,
} from "@components/ValidationContext/ValidationContext";
import {
  ActionButton,
  FilterBar,
  NoneFoundMessage,
  SearchRow,
} from "@components/ViewWrapper/styles";

import {
  getAccessRequestUrl,
  getJobDetailUrl,
  getJobLogsUrl,
  JOBS_PAGE_TITLE,
  LOCAL_STORAGE_KEY_SKIP_JOB_CANCEL_CONFIRMATION,
  SEARCH_DEBOUNCE_MS,
} from "@helpers/constants";
import { JOBS_EVENTS } from "@helpers/events";
import { normalizePathname } from "@helpers/utils";

import { EditableTagHeader } from "../../../components/EditableTagHeader";
import type { RowAction } from "../../../data/types";
import { useDebounce } from "../../../hooks/useDebounce";
import { useRowNavigation } from "../../../hooks/useRowNavigation";
import { useTableStarring } from "../../../hooks/useTableStarring";
import { useTagKey } from "../../../hooks/useTagKey";
import { RESOURCE_NAME_FIELD } from "../../../store/api/resourceTypes";
import { stripNamespacePrefix } from "../../../store/utils/queryStateUtils";
import fieldRenderers from "../data/fieldRenderers";
import type { ResourceField } from "../data/fields";
import {
  defaultSortField,
  fields,
  getDefaultSortOrder,
  jobFieldLabels,
} from "../data/fields";
import type { LocalJob } from "../data/localTypes";
import {
  getJobResourceUsageUrl,
  getStateBackgroundColor as getJobStateBackgroundColor,
  getJobWebAccessUrl,
} from "../utils";

const QUICK_FILTERS = [
  DEFAULT_CHIPS.isMine,
  DEFAULT_CHIPS.isEditable,
  DEFAULT_CHIPS.isToday,
  DEFAULT_CHIPS.isWeek,
];

// Order determines display order in the state filter dropdown.
// Compile-time check: will error if a JobState value is added/removed from the API.
const JOB_STATES = [
  JobState.SUCCEEDED,
  JobState.RUNNING,
  JobState.QUEUING,
  JobState.QUEUED,
  JobState.CANCELLED,
  JobState.CANCELLING,
  JobState.FAILED,
  JobState.INTERRUPTED,
] as const;
{
  // eslint-disable-next-line
  const _exhaustiveCheck: JobState = "" as (typeof JOB_STATES)[number];
  // eslint-disable-next-line
  const _reverseCheck: (typeof JOB_STATES)[number] = "" as JobState;
}

const JobsMonitor = React.memo(function JobsMonitor() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [cancelJobMutation] = useCancelJobMutation();
  const [retryJobMutation] = useRetryJobMutation();
  const [confirmJobCancelOpen, setJobCancelDialogOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState<LocalJob | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [skipCancelConfirmation, setSkipCancelConfirmation] = useState(() => {
    return (
      localStorage.getItem(LOCAL_STORAGE_KEY_SKIP_JOB_CANCEL_CONFIRMATION) ===
      "true"
    );
  });

  const [columnsButton, setColumnsButton] = useState<React.ReactNode>(null);
  const handleColumnsButtonRender = useCallback((button: React.ReactNode) => {
    setColumnsButton(button);
  }, []);

  const { starColumn, starredItems, starredOnly, toggleStarredOnly } =
    useTableStarring<LocalJob>({
      resourceType: "job",
      cacheId: "jobs-monitor",
    });

  const { data: me } = useGetMeQuery();

  const queryState = useQueryStateFromUrl(hydrateJobsQueryState);
  const updateQueryState = useUpdateQueryState(dehydrateJobsQueryState);

  const accountFilterValue =
    queryState.query.filters.get("account")?.valueList[0] ?? null;
  const creatorFilterValue =
    queryState.query.filters.get("creator")?.valueList[0] ?? null;
  const statesFilterValue = (queryState.query.filters.get("state")?.valueList ??
    []) as JobState[];
  const nameFilterValue =
    queryState.query.filters.get(RESOURCE_NAME_FIELD.job)?.fuzzyMatch ?? "";
  const idFilterValue = queryState.query.filters.get("id")?.valueList[0] ?? "";
  const searchValue = nameFilterValue || idFilterValue;

  const {
    handleCreatorChange: baseHandleCreatorChange,
    onSetQuickFilter: baseOnSetQuickFilter,
    quickFilterState,
  } = useFilters<Job>({
    queryState,
    updateQueryState,
    currentUserId: me?.id,
  });

  const [debouncedSearch] = useDebounce(searchValue, SEARCH_DEBOUNCE_MS, {
    leading: true,
  });

  // Only the search field which is typed (id or name) is debounced
  const debouncedQueryState = useMemo(() => {
    if (debouncedSearch === searchValue) return queryState;
    const filters = new Map(queryState.query.filters);
    filters.delete("id");
    filters.delete(RESOURCE_NAME_FIELD.job);
    if (debouncedSearch) {
      // Preserve whichever search type the live filters currently express.
      if (queryState.query.filters.has("id")) {
        filters.set("id", {
          valueList: [debouncedSearch],
          booleanValueList: [],
          fuzzyMatch: null,
          range: [],
        });
      } else {
        filters.set(RESOURCE_NAME_FIELD.job, {
          valueList: [],
          booleanValueList: [],
          fuzzyMatch: debouncedSearch,
          range: [],
        });
      }
    }
    return { ...queryState, query: { ...queryState.query, filters } };
  }, [queryState, debouncedSearch, searchValue]);

  const handleCreatorChange = useCallback(
    (value: string | null) => {
      tracker.event(JOBS_EVENTS.LIST_CREATOR_FILTER_CHANGED);
      baseHandleCreatorChange(value);
    },
    [baseHandleCreatorChange],
  );

  const onSetQuickFilter = useCallback(
    (args: { filter: { filterName: FilterName; isSelected: boolean } }) => {
      tracker.event(
        JOBS_EVENTS.LIST_QUICK_FILTER_TOGGLED,
        args.filter.filterName,
      );
      baseOnSetQuickFilter(args);
    },
    [baseOnSetQuickFilter],
  );

  const apiQueryState = useMemo(() => {
    const nameFilter = debouncedQueryState.query.filters.get(
      RESOURCE_NAME_FIELD.job,
    );
    const fuzzy = nameFilter?.fuzzyMatch;
    if (!fuzzy?.includes(".")) return debouncedQueryState;
    const filters = new Map(debouncedQueryState.query.filters);
    filters.set(RESOURCE_NAME_FIELD.job, {
      ...nameFilter!,
      // Jobs are indexed by short name (e.g. "my_job"), not fullName
      // (e.g. "org.domain.project.my_job"). If the user pastes a fullName, strip
      // the namespace prefix so the API search hits the name field correctly.
      fuzzyMatch: stripNamespacePrefix(fuzzy),
    });
    return {
      ...debouncedQueryState,
      query: { ...debouncedQueryState.query, filters },
    };
  }, [debouncedQueryState]);

  // Create a stable serialized key for RTK Query to detect queryState changes because RTK Query can't compare objects
  const queryKey = useMemo(
    () => dehydrateJobsQueryStateToString(apiQueryState),
    [apiQueryState],
  );

  const {
    currentData: jobsData,
    isFetching,
    isLoading,
    error: jobsError,
  } = useGetJobsQuery({ queryKey }, { pollingInterval: 30000 });

  // Only treat fetching as loading on the first fetch for a given key;
  // background polls should not flash the overlay or empty-state.
  const jobsLoading = isLoading || (isFetching && !jobsData);

  const { data: permissionLookups } = useGetJobsPermissionLevelsQuery(
    { queryKey },
    { pollingInterval: 30000 },
  );

  const { data: totalData } = useGetJobsTotalQuery(
    { queryKey },
    { pollingInterval: 30000 },
  );

  const { handleSortChange, handlePrevPage, handleNextPage } =
    useTableControls<Job>({
      queryState,
      updateQueryState,
    });

  const handleAccountChange = useCallback(
    (accountId: string | null) => {
      tracker.event(JOBS_EVENTS.LIST_ACCOUNT_FILTER_CHANGED);
      const newFilters = new Map(queryState.query.filters);
      if (accountId) {
        newFilters.set("account", {
          valueList: [accountId],
          booleanValueList: [],
          fuzzyMatch: null,
          range: [],
        });
      } else {
        newFilters.delete("account");
      }
      updateQueryState({
        ...queryState,
        query: { ...queryState.query, filters: newFilters },
        paging: { ...queryState.paging, offset: 0 },
      });
    },
    [queryState, updateQueryState],
  );

  const handleStatesChange = useCallback(
    (states: JobState[]) => {
      tracker.event(JOBS_EVENTS.LIST_STATE_FILTER_CHANGED);
      const newFilters = new Map(queryState.query.filters);
      if (states.length > 0) {
        newFilters.set("state", {
          valueList: states,
          booleanValueList: [],
          fuzzyMatch: null,
          range: [],
        });
      } else {
        newFilters.delete("state");
      }
      updateQueryState({
        ...queryState,
        query: { ...queryState.query, filters: newFilters },
        paging: { ...queryState.paging, offset: 0 },
      });
    },
    [queryState, updateQueryState],
  );

  const handleSearchChange = useCallback(
    (filter: SearchFilter) => {
      tracker.event(JOBS_EVENTS.LIST_SEARCH_PERFORMED, filter.type);
      const newFilters = new Map(queryState.query.filters);
      newFilters.delete("id");
      newFilters.delete(RESOURCE_NAME_FIELD.job);

      if (filter.value) {
        if (filter.type === "id") {
          newFilters.set("id", {
            valueList: [filter.value],
            booleanValueList: [],
            fuzzyMatch: null,
            range: [],
          });
        } else {
          newFilters.set(RESOURCE_NAME_FIELD.job, {
            valueList: [],
            booleanValueList: [],
            fuzzyMatch: filter.value,
            range: [],
          });
        }
      }
      updateQueryState({
        ...queryState,
        query: { ...queryState.query, filters: newFilters },
        paging: { ...queryState.paging, offset: 0 },
      });
    },
    [queryState, updateQueryState],
  );

  const handleConfirmJobCancel = () => {
    if (jobToCancel) {
      tracker.event(JOBS_EVENTS.LIST_CANCEL_CONFIRMED);
      cancelJobMutation({ jobId: jobToCancel.id })
        .unwrap()
        .catch((err) => {
          setActionError(
            `Failed to cancel job: ${getErrorMessage(err) ?? "Unknown error"}`,
          );
        });
    }
    setJobCancelDialogOpen(false);
    setJobToCancel(null);
  };

  const handleJobCancelDialogClose = () => {
    setJobCancelDialogOpen(false);
    setJobToCancel(null);
  };

  const handleSkipConfirmationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.checked;
    setSkipCancelConfirmation(newValue);
    localStorage.setItem(
      LOCAL_STORAGE_KEY_SKIP_JOB_CANCEL_CONFIRMATION,
      newValue.toString(),
    );
  };

  const hasJobSetPermission = useCallback(
    (job: LocalJob): boolean => {
      return permissionLookups?.[JOB_PERMISSIONS.edit]?.[job.id] === "ok";
    },
    [permissionLookups],
  );

  const cancelRetryActions = useMemo(
    () => [
      {
        label: "Cancel job",
        getAction(row: LocalJob) {
          const hasPermission = hasJobSetPermission(row);

          return {
            onClick: hasPermission
              ? () => {
                  tracker.event(JOBS_EVENTS.LIST_CANCEL_CLICKED);
                  if (skipCancelConfirmation) {
                    cancelJobMutation({ jobId: row.id })
                      .unwrap()
                      .catch((err) => {
                        setActionError(
                          `Failed to cancel job: ${getErrorMessage(err) ?? "Unknown error"}`,
                        );
                      });
                  } else {
                    setJobToCancel(row);
                    setJobCancelDialogOpen(true);
                  }
                }
              : undefined,
            disabled: !hasPermission,
            disabledTooltip: !hasPermission
              ? "You can't cancel since you don't have set permission for this job"
              : undefined,
          };
        },
        hidden: (row: LocalJob) => {
          return (
            row.state === JobState.SUCCEEDED ||
            row.state === JobState.CANCELLING ||
            !row.alive
          );
        },
        icon: (disabled: boolean) => (
          <HighlightOff
            fontSize="small"
            color={disabled ? "disabled" : "warning"}
          />
        ),
      },
      {
        label: "Retry job",
        getAction(row: LocalJob) {
          const hasPermission = hasJobSetPermission(row);

          return {
            onClick: hasPermission
              ? () => {
                  tracker.event(JOBS_EVENTS.LIST_RETRY_CLICKED);
                  retryJobMutation({ jobId: row.id })
                    .unwrap()
                    .catch((err) => {
                      setActionError(
                        `Failed to retry job: ${getErrorMessage(err) ?? "Unknown error"}`,
                      );
                    });
                }
              : undefined,
            disabled: !hasPermission,
            disabledTooltip: !hasPermission
              ? "You can't retry since you don't have set permission for this job"
              : undefined,
          };
        },
        hidden: (row: LocalJob) => {
          return row.state === JobState.SUCCEEDED || row.alive;
        },
        icon: (disabled: boolean) => (
          <Replay fontSize="small" color={disabled ? "disabled" : "primary"} />
        ),
      },
    ],
    [
      retryJobMutation,
      skipCancelConfirmation,
      cancelJobMutation,
      hasJobSetPermission,
    ],
  );

  const requestAccessJobAction = useMemo(
    () => ({
      label: "Request access",
      getAction(row: LocalJob) {
        return {
          onClick: () => {
            tracker.event(JOBS_EVENTS.LIST_REQUEST_ACCESS_CLICKED);
            navigate(getAccessRequestUrl(row.id));
          },
        };
      },
      hidden: (row: LocalJob) =>
        permissionLookups?.[JOB_PERMISSIONS.view]?.[row.id] === "ok",
      icon: () => <VpnKeyOutlined fontSize="small" color="primary" />,
    }),
    [navigate, permissionLookups],
  );

  const columns = useMemo(
    () =>
      [
        { ...fields.id, width: 118, visibleByDefault: true },
        { ...fields.account, visibleByDefault: true },
        { ...fields.name, visibleByDefault: true },
        {
          ...fields.state,
          contextActions: cancelRetryActions,
          width: 165,
          visibleByDefault: true,
        },
        { ...fields.tags, customHeader: <EditableTagHeader /> },
        { ...fields.elapsed, width: 95, visibleByDefault: true },
        { ...fields.created, visibleByDefault: true },
        { ...fields.endedOn, visibleByDefault: true },
        { ...fields.creator, visibleByDefault: true },
        { ...fields.gpu, width: 90, visibleByDefault: true },
        { ...fields.replicas, width: 95, visibleByDefault: true },
      ].map((column) => ({
        ...column,
        label: jobFieldLabels[column.fieldName],
        defaultSortOrder: getDefaultSortOrder(column),
      })),
    [cancelRetryActions],
  );

  const metadataColumns = useMemo(
    () =>
      columns.map((column) => {
        if (column.fieldName === "id") {
          return {
            ...column,
            contextActions: [requestAccessJobAction],
            contextActionsAlign: "right" as const,
          };
        }
        if (column.fieldName === "state") {
          return { ...column, contextActions: undefined };
        }
        return column;
      }),
    [columns, requestAccessJobAction],
  );

  const basename = normalizePathname(useLocation().pathname);

  const jobs = useMemo(() => jobsData?.items ?? null, [jobsData]);

  // Metadata fallback: when search returns no results, try the metadata endpoint.
  const hasSearchWithNoResults =
    !jobsLoading && jobs?.length === 0 && !!debouncedSearch;
  const { data: metadataResult, isFetching: metadataFetching } =
    useGetResourceMetadataQuery(
      { identifier: debouncedSearch },
      { skip: !hasSearchWithNoResults },
    );

  const metadataItems: LocalJob[] = useMemo(
    () =>
      metadataResult?.type === "job"
        ? [{ state: "unknown", ...metadataResult } as unknown as LocalJob]
        : [],
    [metadataResult],
  );

  const showMetadataFallback =
    hasSearchWithNoResults &&
    !metadataFetching &&
    metadataItems.length > 0 &&
    debouncedSearch === searchValue;

  const isSearchDebouncePending =
    !!searchValue && debouncedSearch !== searchValue;

  const actions: RowAction<LocalJob>[] = useMemo(
    () => [
      // Uncomment this to open job info in the same tab
      // {
      //   label: "Job info",
      //   getAction(row) {
      //     return { internalLink: `/job/${row.id}` };
      //   },
      // },
      {
        label: "Job info",
        getAction(row) {
          return {
            onClick: () => {
              const url = `${window.location.origin}${getJobDetailUrl(row.id)}`;
              window.open(url, "_blank");
            },
          };
        },
      },
      // Uncomment this to open logs in the same tab
      // {
      //   label: "Logs",
      //   getAction(row) {
      //     return { internalLink: `/job/${row.id}/logs` };
      //   },
      // },
      {
        label: "Logs",
        getAction(row) {
          return {
            onClick: () => {
              const url = `${window.location.origin}${getJobLogsUrl(row.id)}`;
              window.open(url, "_blank");
            },
          };
        },
      },
      {
        label: "Copy link to job",
        getAction(row) {
          return {
            onClick: () => {
              const url = `${window.location.origin}${getJobDetailUrl(row.id)}`;
              navigator.clipboard.writeText(url);
            },
          };
        },
      },
      {
        label: "Web access",
        getAction(row) {
          if (row.state !== "RUNNING") {
            return { externalLink: null };
          }
          return { externalLink: getJobWebAccessUrl(row) };
        },
      },
      {
        label: "Resource usage",
        getAction(row) {
          return { externalLink: getJobResourceUsageUrl(row) };
        },
      },
      ...cancelRetryActions,
    ],
    [cancelRetryActions],
  );

  const baseHandleRowClick = useRowNavigation<Job>((row) =>
    getJobDetailUrl(row.id),
  );
  const handleRowClick = useCallback(
    (row: Job, event?: React.MouseEvent<Element> | React.KeyboardEvent) => {
      tracker.event(JOBS_EVENTS.LIST_ROW_CLICKED);
      return baseHandleRowClick(row, event);
    },
    [baseHandleRowClick],
  );

  const paging = useMemo(
    () => ({
      ...queryState.paging,
      total: totalData,
    }),
    [queryState.paging, totalData],
  );

  const { validationContextValue } = useValidationContextValue();

  // Use tag key to force DataTable re-render when tag key changes
  const { tagKey } = useTagKey();

  // Initialize URL params with default columns before DataTable renders
  useDataTableUrlInit({
    tableId: "jobs-monitor",
    enableColumnSelection: true,
    allColumns: columns,
  });

  return (
    <>
      <ViewWrapper
        docsSection="JOBS"
        title={JOBS_PAGE_TITLE}
        aboveContent={
          <>
            <ValidationContext.Provider value={validationContextValue}>
              <FilterBar>
                <AccountSelector
                  value={accountFilterValue}
                  onChange={handleAccountChange}
                />
                <UserRoleSelector
                  value={creatorFilterValue}
                  onChange={handleCreatorChange}
                />
                <StateSelector
                  value={statesFilterValue}
                  onChange={handleStatesChange}
                  options={JOB_STATES}
                  getStateColor={getJobStateBackgroundColor}
                />
              </FilterBar>
            </ValidationContext.Provider>
          </>
        }
      >
        <>
          <SearchRow>
            <NameIdSearch value={searchValue} onChange={handleSearchChange} />
            <ActionButton
              color="primary"
              variant="contained"
              component={Link}
              to={`${basename}/new`}
              state={{ canGoBack: true }}
              onClick={() => dispatch(resetNewJobForm())}
            >
              New Job
            </ActionButton>
          </SearchRow>
          <QuickFilters
            filters={QUICK_FILTERS}
            filterState={quickFilterState}
            setQuickFilter={(filter) => onSetQuickFilter({ filter })}
            leadingChip={
              <StarredFilterChip
                active={starredOnly}
                onClick={toggleStarredOnly}
              />
            }
            columnsButton={columnsButton}
          />
          {starredOnly ? (
            starredItems.length === 0 ? (
              <NoneFoundMessage>
                You haven&apos;t starred any jobs yet. Hover a row and click the
                star to add one.
              </NoneFoundMessage>
            ) : (
              <DataTable<Job, LocalJob, ResourceField<LocalJob>>
                key={`jobs-table-starred-${tagKey}`}
                ariaLabel="Starred jobs table"
                columns={columns}
                data={starredItems}
                fieldRenderers={fieldRenderers}
                sort={queryState.query.sort}
                paging={{
                  offset: 0,
                  limit: starredItems.length,
                  total: {
                    count: starredItems.length,
                    isCountFinal: true,
                  },
                }}
                setSort={() => {}}
                onPrevPageClick={() => {}}
                onNextPageClick={() => {}}
                onRowClick={handleRowClick}
                actions={actions}
                starColumn={starColumn}
                enableColumnSelection={true}
                tableId="jobs-monitor"
                renderColumnsButtonExternally={true}
                onRenderColumnsButton={handleColumnsButtonRender}
              />
            )
          ) : (
            <>
              <LoadOverlay
                loading={
                  jobsLoading || metadataFetching || isSearchDebouncePending
                }
              />
              {jobsError && (
                <p>
                  ERROR:{" "}
                  {jobsError instanceof Error
                    ? jobsError.message
                    : "Failed to load jobs"}
                </p>
              )}
              {!jobsLoading &&
                !jobsError &&
                jobs?.length === 0 &&
                !showMetadataFallback &&
                !metadataFetching &&
                !isSearchDebouncePending && (
                  <NoneFoundMessage>
                    No jobs found for this query. Try adjusting your filters.
                  </NoneFoundMessage>
                )}
              {!jobsError && showMetadataFallback && (
                <DataTable<Job, LocalJob, ResourceField<LocalJob>>
                  key={`jobs-table-metadata-${tagKey}`}
                  ariaLabel="Jobs table"
                  columns={metadataColumns}
                  data={metadataItems}
                  fieldRenderers={fieldRenderers}
                  sort={queryState.query.sort}
                  paging={{
                    offset: 0,
                    limit: queryState.paging.limit,
                    total: {
                      count: metadataItems.length,
                      isCountFinal: true,
                    },
                  }}
                  setSort={handleSortChange}
                  onPrevPageClick={handlePrevPage}
                  onNextPageClick={handleNextPage}
                  onCellCopied={(fieldName) =>
                    tracker.event(JOBS_EVENTS.LIST_CELL_COPIED, fieldName)
                  }
                  actions={[]}
                  starColumn={starColumn}
                  enableColumnSelection={true}
                  tableId="jobs-monitor"
                  renderColumnsButtonExternally={true}
                  onRenderColumnsButton={handleColumnsButtonRender}
                  defaultSort={{
                    fieldName: defaultSortField.fieldName,
                    direction: getDefaultSortOrder(defaultSortField),
                  }}
                />
              )}
              {!jobsError && jobs && jobs.length > 0 && (
                <DataTable<Job, LocalJob, ResourceField<LocalJob>>
                  key={`jobs-table-${tagKey}`}
                  ariaLabel="Jobs table"
                  columns={columns}
                  data={jobs}
                  fieldRenderers={fieldRenderers}
                  sort={queryState.query.sort}
                  paging={paging}
                  setSort={handleSortChange}
                  onPrevPageClick={handlePrevPage}
                  onNextPageClick={handleNextPage}
                  onRowClick={handleRowClick}
                  onCellCopied={(fieldName) =>
                    tracker.event(JOBS_EVENTS.LIST_CELL_COPIED, fieldName)
                  }
                  actions={actions}
                  starColumn={starColumn}
                  enableColumnSelection={true}
                  tableId="jobs-monitor"
                  renderColumnsButtonExternally={true}
                  onRenderColumnsButton={handleColumnsButtonRender}
                  defaultSort={{
                    fieldName: defaultSortField.fieldName,
                    direction: getDefaultSortOrder(defaultSortField),
                  }}
                />
              )}
            </>
          )}
        </>
      </ViewWrapper>
      <Dialog
        open={confirmJobCancelOpen}
        onClose={handleJobCancelDialogClose}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">
          Confirm Job Cancellation
        </DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <DialogContentText id="cancel-dialog-description">
            Are you sure you want to cancel job &quot;
            {jobToCancel?.name ?? jobToCancel?.id}&quot;? This action cannot be
            undone.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={skipCancelConfirmation}
                onChange={handleSkipConfirmationChange}
                color="primary"
              />
            }
            label="Don't ask me again"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ pb: 3, pr: 3, gap: 1 }}>
          <Button
            onClick={handleJobCancelDialogClose}
            color="primary"
            sx={{ px: 3 }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirmJobCancel}
            color="warning"
            variant="contained"
            autoFocus
            sx={{ px: 3 }}
          >
            Confirm Cancellation
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!actionError}
        autoHideDuration={6000}
        onClose={() => setActionError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      </Snackbar>
    </>
  );
});

export default JobsMonitor;
